import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  BsBuilding,
  BsFileEarmarkText,
  BsCurrencyDollar,
  BsGraphUpArrow,
} from "react-icons/bs";
import axios from "axios";
import urls from "../../Utils/urls";
import { getHeaders } from "../Documentation/utils";
import "./DrawStats.css";

const currency = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  maximumFractionDigits: 0,
});

const coerceNumber = (val) => {
  if (val == null || val === "") return 0;
  const n = Number(val);
  return Number.isFinite(n) ? n : 0;
};

const normalizeSummary = (summary, projects) => {
  const fallbackTotals = projects.reduce(
    (acc, p) => {
      acc.totalBudget += coerceNumber(p.budget || p.Budget || p.total_budget);
      acc.totalFunded += coerceNumber(p.funded || p.Funded || p.total_funded);
      acc.totalHoldback += coerceNumber(p.holdback || p.Holdback || p.remaining_holdback);
      return acc;
    },
    { totalBudget: 0, totalFunded: 0, totalHoldback: 0 },
  );

  return {
    activeProjects:
      coerceNumber(summary?.activeProjects ?? summary?.active_projects ?? summary?.["Active Projects"]) ||
      projects.length,
    totalBudget:
      coerceNumber(summary?.totalBudget ?? summary?.total_budget ?? summary?.["Total Budget"]) ||
      fallbackTotals.totalBudget,
    totalFunded:
      coerceNumber(summary?.totalFunded ?? summary?.total_funded ?? summary?.["Total Funded"]) ||
      fallbackTotals.totalFunded,
    remainingHoldback:
      coerceNumber(summary?.remainingHoldback ?? summary?.remaining_holdback ?? summary?.["Remaining Holdback"]) ||
      fallbackTotals.totalHoldback,
  };
};

const DrawStats = () => {
  const navigate = useNavigate();
  const selectedAccountLenderId = useSelector((state) => state.account.lenderId);
  const accountOptions = useSelector((state) => state.account.accountOptions);

  const [summary, setSummary] = useState(null);
  const [projects, setProjects] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const allLenderRecIds =
    accountOptions?.length > 0
      ? accountOptions.map((a) => a.value).join(" ")
      : selectedAccountLenderId;

  useEffect(() => {
    let cancelled = false;

    async function fetchStats() {
      setIsLoading(true);
      try {
        const res = await axios.post(
          urls.draws.getDetailsDrawDashboard(),
          { lenderrecid: allLenderRecIds },
          getHeaders(true),
        );
        if (cancelled) return;

        const rawData = res?.data ?? {};
        const payload = rawData?.data ?? rawData?.result ?? rawData;
        const isArrayPayload = Array.isArray(payload);
        const summaryFromArray =
          isArrayPayload && Array.isArray(payload?.[0]) ? payload[0]?.[0] : null;
        const summaryData = isArrayPayload
          ? summaryFromArray || {}
          : payload?.summary || payload?.dashboardSummary || payload?.metrics || payload?.overview || {};
        const projectsRaw = isArrayPayload
          ? payload?.[1] || []
          : payload?.projects || payload?.loans || payload?.properties || payload?.data || payload?.items || [];

        setSummary(summaryData);
        setProjects(Array.isArray(projectsRaw) ? projectsRaw : []);
      } catch (err) {
        if (!cancelled) console.error("DrawStats: failed to load", err);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    }

    fetchStats();
    return () => { cancelled = true; };
  }, [allLenderRecIds]);

  const stats = useMemo(() => {
    const s = normalizeSummary(summary || {}, projects);
    const ready = !isLoading && (projects.length > 0 || summary !== null);
    return [
      { label: "Active Projects", value: ready ? s.activeProjects.toString() : "--", icon: <BsBuilding size={18} />, cls: "blue" },
      { label: "Total Budget", value: ready ? currency.format(s.totalBudget) : "--", icon: <BsFileEarmarkText size={18} />, cls: "sky" },
      { label: "Total Funded", value: ready ? currency.format(s.totalFunded) : "--", icon: <BsCurrencyDollar size={18} />, cls: "green" },
      { label: "Available Funds", value: ready ? currency.format(s.remainingHoldback) : "--", icon: <BsGraphUpArrow size={18} />, cls: "orange" },
    ];
  }, [summary, projects, isLoading]);

  return (
    <div
      className="draw-stats-card"
      onClick={() => navigate("/lender-draws-dashboard")}
    >
      <div className="draw-stats-header">
        <h6>Construction Draws</h6>
        <span className="view-link">View Details &rarr;</span>
      </div>
      <div className="draw-stats-grid">
        {stats.map((s) => (
          <div key={s.label} className="draw-stat-item">
            <div className={`draw-stat-icon ${s.cls}`}>{s.icon}</div>
            <div>
              <div className="draw-stat-label">{s.label}</div>
              <div className="draw-stat-value">{s.value}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default DrawStats;
